import React from "react";
import { animated } from "react-spring";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";

import Notification from "./Notification";
import useStyles from "./NotificationStack.styles";
import type { NotificationItem } from "./types";
import useTransitions from "./useTransitions";

const AnimatedNotification = animated(Notification);

export interface NotificationStackProps<D extends NotificationItem> {
  stack: D[];
  renderItem: (item: D, index?: number, showStack?: boolean) => React.ReactElement;
  itemHeight: number;
  notifications?: D[];
  topPosition?: number;
  rightPosition?: number;
  expandStack?: boolean;
  stackMessage?: string;
  hideStackMessage?: string;
  closeNotificationsMessage?: string;
  onClose?: () => void;
  stackLimit?: number;
  notificationLimit?: number;
}

const paddingHeightPx = 16;

const NotificationStack = <D extends NotificationItem>({
  stack,
  notifications,
  renderItem,
  itemHeight,
  topPosition = 16,
  rightPosition = 16,
  expandStack = false,
  stackMessage,
  onClose,
  stackLimit = 10,
  notificationLimit = 10,
  hideStackMessage = "Show less",
  closeNotificationsMessage = "Close all",
}: NotificationStackProps<D>): JSX.Element => {
  const classes = useStyles();

  const [showStack, setShowStack] = React.useState<boolean>(expandStack);
  const messageRef = React.useRef<HTMLDivElement>(null);
  const [stackHeight, setStackHeight] = React.useState<number>(0);
  const actionsRef = React.useRef<HTMLDivElement>(null);
  const [actionsHeight, setActionsHeight] = React.useState<number>(0);

  const { stackTransitions, notificationTransitions, actionTransitions } = useTransitions(
    stack.slice(0, stackLimit),
    showStack,
    itemHeight,
    actionsHeight,
    notifications?.slice(0, notificationLimit) ?? [],
  );

  const isStackMultiple = stack.length > 1;

  React.useEffect(() => {
    setShowStack(expandStack);
  }, [expandStack]);

  React.useEffect(() => {
    if (!showStack) {
      setStackHeight(itemHeight + paddingHeightPx + (messageRef.current?.clientHeight ?? 0));
    }
  }, [itemHeight, stack, showStack]);

  React.useEffect(() => {
    if (showStack) {
      setActionsHeight(actionsRef.current?.clientHeight ?? 0);
    }
  }, [showStack]);

  const renderStack = (): React.ReactElement => {
    if (!stack.length) return <></>;

    return (
      <Box
        data-testid="stack"
        className={clsx(classes.wrapper, isStackMultiple ? classes.stackOffset : classes.stack)}
        height={stackHeight}
      >
        <Box data-testid="stack-container">
          {stackTransitions((style, item, _, index) => {
            const firstItem = index === 0;
            return (
              <AnimatedNotification
                data-testid={`notification-${item.key}`}
                className={clsx(index > 1 && !showStack && classes.clearShadow, firstItem && classes.firstItem)}
                style={style}
              >
                {renderItem(item, index, showStack)}
                {firstItem && !showStack && isStackMultiple && (
                  <div
                    aria-hidden="true"
                    data-testid="show-stack"
                    ref={messageRef}
                    className={classes.action}
                    onClick={() => setShowStack(true)}
                  >
                    {stackMessage}
                  </div>
                )}
              </AnimatedNotification>
            );
          })}
        </Box>
        {actionTransitions(
          (style, item) =>
            item && (
              <animated.div ref={actionsRef} className={classes.actions} style={style}>
                <Typography data-testid="hide-stack" className={classes.action} onClick={() => setShowStack(false)}>
                  {hideStackMessage}
                </Typography>
                <Typography data-testid="close-stack" className={classes.action} onClick={onClose}>
                  {closeNotificationsMessage}
                </Typography>
              </animated.div>
            ),
        )}
      </Box>
    );
  };

  const renderNotifications = (): React.ReactElement => {
    if (showStack || !notifications) return <></>;

    return (
      <Box data-testid="notifications" className={clsx(classes.wrapper, classes.notifications)}>
        {notificationTransitions(
          (style, item) =>
            item && (
              <AnimatedNotification data-testid={`notification-${item.key}`} style={style}>
                {renderItem(item)}
              </AnimatedNotification>
            ),
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.root} top={topPosition} right={rightPosition}>
      {renderStack()}
      {renderNotifications()}
    </Box>
  );
};

export default NotificationStack;
